/**
 * StartBattle use case (contracts.md): validate, create session, set game scene.
 */

import { createSession, type GameSession, type SkillEffects } from "../../domain/session.js";
import type { FieldId } from "../../domain/types.js";
import type { StartBattleResult } from "../types.js";
import type { StageSettings } from "../../domain/stage-settings.js";
import type { RenderPort, AudioPort, FieldConfigPort } from "../ports.js";
import type { MapProgress } from "../../domain/map-progress.js";
import { isFieldUnlocked } from "../../domain/map-progress.js";
import { sessionToView } from "../state-view.js";
import type { MageProfile } from "../../domain/mage-profile.js";
import { getSkillEffectiveValue } from "../../domain/skills.js";
import { SkillId } from "../../domain/skills.js";
import { levelFromXp } from "../../domain/mage-xp.js";
import { availableSkillPoints } from "../../domain/mage-profile.js";

export interface StartBattleDeps {
  fieldConfig: FieldConfigPort;
  render: RenderPort;
  audio: AudioPort;
  setSession(session: GameSession | null): void;
  getMapProgress?: () => MapProgress | undefined;
  getMageProfile?: () => MageProfile;
  /** Called each time a new battle is started (not on loadProgress). */
  incrementBattles?: () => void;
}

function buildSkillEffects(profile: MageProfile, progress?: MapProgress): SkillEffects {
  const levels = profile.skillLevels;
  const value = (skillId: number): number => getSkillEffectiveValue(skillId, levels[skillId] ?? 0);
  const completedFieldCount = progress?.completedFieldIds.size ?? 0;
  return {
    moreInitialMana: value(SkillId.MORE_INITIAL_MANA),
    manaReplenish: value(SkillId.MANA_REPLENISH),
    banishManaCost: value(SkillId.BANISH_MANA_COST),
    lowerGemCost: value(SkillId.LOWER_CREATING_AND_COMBINING_MANA_COST),
    buildingCost: value(SkillId.BUILDING_COST),
    demolition: value(SkillId.DEMOLITION),
    poisonMastery: value(SkillId.POISON_MASTERY),
    bloodboundMastery: value(SkillId.BLOODBOUND_MASTERY),
    violentExplosions: value(SkillId.VIOLENT_EXPLOSIONS),
    multipleDamageMastery: value(SkillId.MULTIPLE_DAMAGE_MASTERY),
    chainHitMastery: value(SkillId.CHAIN_HIT_MASTERY),
    amplifierPower: value(SkillId.AMPLIFIER_POWER),
    cheaperManaPoolAndUnlock: value(SkillId.CHEAPER_MANA_POOL_AND_UNLOCK),
    dualGemMastery: value(SkillId.DUAL_GEM_MASTERY),
    tripleGemMastery: value(SkillId.TRIPLE_GEM_MASTERY),
    slowMastery: value(SkillId.SLOW_MASTERY),
    armorTearingMastery: value(SkillId.ARMOR_TEARING_MASTERY),
    damage: value(SkillId.DAMAGE),
    shockingMastery: value(SkillId.SHOCKING_MASTERY),
    manaGatheringMastery: value(SkillId.MANA_GATHERING_MASTERY),
    traps: value(SkillId.TRAPS),
    wildStarterGem: value(SkillId.WILD_STARTER_GEM),
    gemDeploymentTime: value(SkillId.GEM_DEPLOYMENT_TIME),
    firingSpeed: value(SkillId.FIRING_SPEED),
    range: value(SkillId.RANGE),
    badgesToMana: value(SkillId.BADGES_TO_MANA) * completedFieldCount,
    shrines: value(SkillId.SHRINES),
  };
}

export function createStartBattle(deps: StartBattleDeps) {
  return function startBattle(
    fieldId: FieldId,
    stageSettings?: StageSettings
  ): StartBattleResult {
    if (!fieldId || typeof fieldId !== "string") {
      return { code: "ValidationError", reason: "invalid field" };
    }
    const progress = deps.getMapProgress?.();
    if (progress && !isFieldUnlocked(progress, fieldId)) {
      return { code: "ValidationError", reason: "FIELD_LOCKED" };
    }
    const config = deps.fieldConfig.getFieldConfig(fieldId);
    if (config === null) {
      return { code: "ValidationError", reason: "FIELD_NOT_FOUND" };
    }
    const profile = deps.getMageProfile?.();
    const skillEffects = profile ? buildSkillEffects(profile, progress) : undefined;
    const session = createSession(fieldId, config, skillEffects, stageSettings);
    deps.setSession(session);
    deps.incrementBattles?.();
    deps.render.setScene("game");
    deps.render.update(sessionToView(session, "game"));
    deps.audio.play("start");
    return { ok: true };
  };
}

// Re-export for tests
export { availableSkillPoints, levelFromXp };
